<template>
  <div>
    <vx-card>
      <div id="edi-list-view" class="data-list-container">
        <vs-table
          noDataText="Nenhum log encontrado"
          class="tablePrincipal"
          ref="table"
          multiple
          v-model="selected"
          :data="logs"
        >
          <div slot="header" class="w-full mb-3">
            <div
              class="flex flex-wrap-reverse items-center flex-grow justify-between"
            >
              <div class="flex items-center">
                <vs-button
                  v-if="$can($actionsDefault.Escrita, subjectPermission)"
                  @click="handleButtonClick"
                  :color="file ? 'success' : 'primary'"
                  :type="file ? 'filled' : 'border'"
                  class="h-12 mr-4"
                >
                  <feather-icon icon="UploadIcon" svgClasses="h-4 w-4" />
                  <span class="ml-2">{{ buttonLabel }}</span>
                </vs-button>

                <input
                  v-if="$can($actionsDefault.Escrita, subjectPermission)"
                  type="file"
                  ref="fileInput"
                  data-testid="edi-file-input"
                  @change="handleFileChange"
                  accept=".txt,text/plain"
                  style="display: none"
                />

                <vs-button
                  @click="getLogs"
                  color="primary"
                  type="border"
                  icon="refresh"
                  class="h-12"
                >
                </vs-button>
              </div>
              <div class="flex flex-wrap items-center">
                <PaginationItemsPerPageDropdown
                  :items-per-page="itemsPerPage"
                  :total-items="totalItems"
                  :current-page="currentPage"
                  @update:itemsPerPage="itemsPerPage = $event"
                />
                <vs-input
                  class="ml-3"
                  icon="search"
                  placeholder="Buscar"
                  v-model="searchFileName"
                />
              </div>
            </div>
          </div>

          <template slot="thead">
            <vs-th>Arquivo Enviado</vs-th>
            <vs-th>Adquirente</vs-th>
            <vs-th>Tipo de Arquivo</vs-th>
            <vs-th>Data do Envio</vs-th>
            <vs-th>Usuário</vs-th>
            <vs-th>Status</vs-th>
            <vs-th>Retorno</vs-th>
          </template>

          <template v-slot="{ data }">
            <tbody>
              <vs-tr :data="log" :key="log.id" v-for="log in data">
                <vs-td>
                  <p class="product-category" v-if="!getFileUrl(log)">
                    {{ getFileName(log) }}
                  </p>
                  <p class="product-category" v-else>
                    <a
                      :href="getFileUrl(log)"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <feather-icon icon="DownloadIcon" svgClasses="h-4 w-4" />
                      {{ getFileName(log) }}
                    </a>
                  </p>
                </vs-td>

                <vs-td data-testid="edi-acquirer-cell">
                  <p class="product-category">
                    {{ getAcquirer(log) }}
                  </p>
                </vs-td>

                <vs-td data-testid="edi-file-type-cell">
                  <p class="product-category">
                    {{ getFileType(log) }}
                  </p>
                </vs-td>

                <vs-td>
                  <p class="product-category">
                    {{ formatDate(log) }}
                  </p>
                </vs-td>

                <vs-td>
                  <p class="product-category">
                    {{ getUserName(log) }}
                  </p>
                </vs-td>

                <vs-td data-testid="edi-status-cell">
                  <vs-chip :color="getStatusColor(log.status)">
                    {{ getStatus(log) }}
                  </vs-chip>
                </vs-td>

                <vs-td>
                  <a
                    v-if="log.logFileUrl"
                    :href="log.logFileUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <feather-icon icon="Download" svgClasses="h-4 w-4" />
                    {{ extractFileName(log.logFileUrl) }}
                  </a>
                  <vs-button
                    v-else-if="
                      log.status === EDI_IMPORT_STATUS_ENUM.CREATED ||
                      log.status === EDI_IMPORT_STATUS_ENUM.PROCESSING
                    "
                    @click="reinsertInQueue(log)"
                    color="primary"
                    type="border"
                    >Reprocessar</vs-button
                  >
                  <p v-else-if="!log.errorMessage" class="product-category">
                    -
                  </p>

                  <p v-if="log.errorMessage" class="text-danger mt-1">
                    {{ log.errorMessage }}
                  </p>
                </vs-td>
              </vs-tr>
            </tbody>
          </template>
        </vs-table>

        <div class="mt-6">
          <vs-pagination
            :total="totalPages"
            v-model="currentPage"
          ></vs-pagination>
        </div>
      </div>
    </vx-card>
  </div>
</template>

<script>
import moment from 'moment';
import ediApi from '../../../services/edi.api';
import notify from '../../../utils/notify';
import functions from '../../../utils/functions';
import PaginationItemsPerPageDropdown from '../../../components/PaginationItemsPerPageDropdown.vue';
import {
  EDI_ACQUIRER_LABELS,
  EDI_FILE_TYPE_LABELS,
  EDI_IMPORT_STATUS_ENUM,
  EDI_INVALID_ACQUIRER_LABEL,
} from '@/utils/constants';

export default {
  name: 'EdiFiles',
  components: { PaginationItemsPerPageDropdown },
  data() {
    return {
      logs: [],
      totalItems: 0,
      itemsPerPage: 50,
      currentPage: 1,
      selected: [],
      file: null,
      searchFileName: '',
      subjectPermission: 'Arquivo EDI',
      buttonLabel: 'Selecionar Arquivo',
      statusColor: {
        created: 'primary',
        processing: 'warning',
        done: 'success',
        error: 'danger',
      },
      statusLabel: {
        created: 'Criado',
        processing: 'Processando',
        done: 'Sucesso',
        error: 'Erro',
      },
      debounceGetLogs: null,
    };
  },
  computed: {
    EDI_IMPORT_STATUS_ENUM() {
      return EDI_IMPORT_STATUS_ENUM;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.totalItems / this.itemsPerPage));
    },
  },
  mounted() {
    this.debounceGetLogs = functions.debounce(this.getLogs, 500);
    this.getLogs();
  },
  methods: {
    pickFirst(log, keys, fallback = '-') {
      if (!log || typeof log !== 'object') {
        return fallback;
      }
      for (let index = 0; index < keys.length; index += 1) {
        const key = keys[index];
        if (log[key] !== undefined && log[key] !== null && log[key] !== '') {
          return log[key];
        }
      }
      return fallback;
    },
    extractFileName(url) {
      if (!url || !url.includes('Z-')) return url;
      return url.split('Z-')[1];
    },
    async getLogs(showLoading = true) {
      try {
        if (showLoading) {
          this.$vs.loading();
        }
        const response = await ediApi.getLogs({
          page: this.currentPage,
          items_per_page: this.itemsPerPage,
          search: this.searchFileName || undefined,
        });
        this.logs = response.items;
        this.totalItems = response.total;
      } catch (error) {
        notify.notifyFail(
          this.$vs,
          'Não foi possível carregar os logs do EDI.',
        );
      } finally {
        if (showLoading) {
          this.$vs.loading.close();
        }
      }
    },
    handleButtonClick() {
      if (!this.file) {
        this.$refs.fileInput.click();
      } else {
        this.uploadFile();
      }
    },
    handleFileChange(event) {
      this.file = event.target.files[0];
      if (!this.file) {
        this.buttonLabel = 'Selecionar Arquivo .txt';
        return;
      }
      if (!this.file.name.toLowerCase().endsWith('.txt')) {
        notify.notifyFail(this.$vs, 'Selecione um arquivo .txt');
        this.file = null;
        this.buttonLabel = 'Selecionar Arquivo .txt';
        event.target.value = '';
        return;
      }
      this.buttonLabel = 'Enviar Arquivo';
    },
    async uploadFile() {
      if (!this.file) {
        return;
      }
      try {
        this.$vs.loading();
        await ediApi.uploadFile(this.file);
        notify.notifySuccess(this.$vs, 'Arquivo enviado com sucesso.');
        this.file = null;
        this.buttonLabel = 'Selecionar Arquivo .txt';
        this.$refs.fileInput.value = '';
        await this.getLogs(false);
      } catch (error) {
        notify.notifyFail(this.$vs, 'Não foi possível enviar o arquivo.');
      } finally {
        this.$vs.loading.close();
      }
    },
    async reinsertInQueue(log) {
      try {
        this.$vs.loading();
        await ediApi.reinsertInQueue(log.id);
        notify.notifySuccess(
          this.$vs,
          'Arquivo inserido na fila de processamento',
        );
      } catch (error) {
        notify.notifyFail(
          this.$vs,
          'Não foi possível inserir o arquivo na fila.',
        );
      } finally {
        this.$vs.loading.close();
      }
    },
    formatDate(log) {
      const date = this.pickFirst(log, ['createdAt']);
      if (!date || date === '-') {
        return '-';
      }
      return functions.formatDateAndHour(date);
    },
    getStatus(log) {
      return this.statusLabel[this.pickFirst(log, ['status'])] || '-';
    },
    getStatusColor(status) {
      return this.statusColor[status] || 'primary';
    },
    getFileUrl(log) {
      return this.pickFirst(log, ['fileUrl', 'file_url'], '');
    },
    getFileName(log) {
      const fileName = this.pickFirst(log, [
        'fileName',
        'file_name',
        'filename',
      ]);
      if (fileName !== '-') {
        return fileName;
      }
      const url = this.getFileUrl(log);
      if (!url) {
        return '-';
      }
      const parts = String(url).split('/');
      return parts[parts.length - 1];
    },
    getUserName(log) {
      if (log && log.user && log.user.name) {
        return log.user.name;
      }
      return 'System';
    },
    getAcquirer(log) {
      const acquirer =
        log && log.creditCardAcquirer ? log.creditCardAcquirer.name : null;
      if (acquirer) {
        return EDI_ACQUIRER_LABELS[acquirer] || acquirer;
      }
      if (log && log.status === EDI_IMPORT_STATUS_ENUM.ERROR) {
        return EDI_INVALID_ACQUIRER_LABEL;
      }
      return '-';
    },
    getFileType(log) {
      const fileType = this.pickFirst(log, ['fileType', 'file_type']);
      return EDI_FILE_TYPE_LABELS[fileType] || '-';
    },
  },
  watch: {
    currentPage() {
      this.getLogs();
    },
    itemsPerPage() {
      if (this.currentPage !== 1) {
        this.currentPage = 1;
        return;
      }
      this.getLogs();
    },
    searchFileName() {
      if (this.currentPage !== 1) {
        this.currentPage = 1;
      }
      this.debounceGetLogs();
    },
  },
};
</script>

<style lang="scss">
#edi-list-view {
  .vs-con-table {
    .vs-table--header {
      display: flex;
      flex-wrap: wrap-reverse;
      margin-left: 1.5rem;
      margin-right: 1.5rem;
      > span {
        display: flex;
        flex-grow: 1;
      }
    }

    .vs-table {
      border-collapse: separate;
      border-spacing: 0 1.3rem;
      padding: 0 1rem;

      tr {
        box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.05);
        td {
          padding: 20px;
          &:first-child {
            border-top-left-radius: 0.5rem;
            border-bottom-left-radius: 0.5rem;
          }
          &:last-child {
            border-top-right-radius: 0.5rem;
            border-bottom-right-radius: 0.5rem;
          }
        }
      }
    }

    .vs-table--thead {
      th {
        padding-top: 0;
        padding-bottom: 0;

        .vs-table-text {
          text-transform: uppercase;
          font-weight: 600;
        }
      }
      tr {
        background: none;
        box-shadow: none;
      }
    }

    .vs-table--pagination {
      justify-content: center;
    }
  }
}
</style>
